// Running processes, listed in the page's status bar (#procs; else under the header, #top): a fit, loading scans, a commit, Publish, the
// background copy… each with what it is at, a progress bar, and a Cancel (✕) when it can be stopped.
// Pages without a header still track them; nothing is shown there. Each one shown goes into the status history when it ends.

import { logStatus } from './statusbar.ts';

export interface Proc {
  /** What it is at now, and how far (0-1; null: unknown). */
  set(text: string, frac?: number | null): void;
  /** It is over (done, failed or cancelled): off the list. */
  end(): void;
}

interface Opts {
  /** Stops it; absent: it can't be stopped, and says why in its tooltip (cantStop). */
  cancel?: () => void;
  cantStop?: string;
  /** Only shown once it has run this many ms (a commit that is usually quick). */
  delay?: number;
}

const live = new Set<HTMLElement>();

/** The list in the status bar, or made under #top on first use; null on a page with neither. */
function host(): HTMLElement | null {
  let h = document.getElementById('procs');
  if (h) return h;
  const top = document.getElementById('top');
  if (!top) return null;
  h = document.createElement('div');
  h.id = 'procs';
  top.append(h);
  return h;
}

/** A process starting: shown in the status bar (or header) until end(). */
export function proc(title: string, o: Opts = {}): Proc {
  const chip = document.createElement('span'), name = document.createElement('span'), bar = document.createElement('progress');
  chip.className = 'proc';
  name.className = 'proc-t';
  bar.max = 1;
  chip.append(name, bar);
  const name0 = title, t0 = performance.now();
  let text = '', frac: number | null = null, ended = false, stopping = false;
  const draw = () => {
    const pct = frac === null ? '' : ` · ${Math.round(100 * frac)}%`;
    name.textContent = `${title}${pct}`;
    chip.title = `${title}${text ? `: ${text}` : ''}${pct}${o.cancel ? '' : `\n(${o.cantStop ?? 'can\'t be stopped midway'})`}`;
    if (frac === null) bar.removeAttribute('value'); else bar.value = frac;
  };
  if (o.cancel) {
    const x = document.createElement('button');
    x.className = 'small';
    x.textContent = '✕';
    x.title = `Cancel ${title}`;
    x.onclick = () => {
      if (stopping) return;
      stopping = true; x.disabled = true; title = `${title}: cancelling`; draw();
      o.cancel!();
    };
    chip.append(x);
  }
  draw();
  const show = () => { if (ended) return; live.add(chip); host()?.append(chip); };
  const t = o.delay ? setTimeout(show, o.delay) : (show(), 0);
  return {
    set(t2, f = null) { if (ended) return; text = t2; frac = f === null ? null : Math.max(0, Math.min(1, f)); draw(); },
    end() {
      if (ended) return;
      ended = true; clearTimeout(t);
      if (live.delete(chip)) {
        const s = (performance.now() - t0) / 1000;
        logStatus(`${name0}: ${stopping ? 'cancelled' : 'ended'} after ${s < 60 ? `${s.toFixed(s < 10 ? 1 : 0)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`}`);
      }
      chip.remove();
    },
  };
}

/** How many are running (for tests). */
export const running = () => live.size;
